import React, { useState, useEffect } from 'react';
import styles from '../styles/SessionBanner.module.css';

/**
 * SessionBanner - Displays JOIN prompt when session is ready
 *
 * Only shown when session is joinable (30 minutes before to 10 minutes after start).
 * Props:
 * - session: Session object with coach, scheduled_start, etc.
 * - onJoin: Callback when join button is clicked
 * - onDismiss: Callback when dismiss button is clicked
 */
function SessionBanner({ session, onJoin }) {
  const [timeUntilSession, setTimeUntilSession] = useState(null);
  const [minutesRemaining, setMinutesRemaining] = useState(session?.minutesRemaining || 0);
  const [canJoin, setCanJoin] = useState(session?.isJoinable || false);

  useEffect(() => {
    if (!session) return;

    // Use server-calculated minutesRemaining
    const updateDisplay = () => {
      const mins = minutesRemaining;

      // Update join status (30 minutes before to 10 minutes after)
      setCanJoin(mins >= -10 && mins <= 30);

      // Format time display (compact format to fit in limited width)
      if (mins < -10) {
        // Session has passed
        setTimeUntilSession(null);
      } else if (mins < 0) {
        setTimeUntilSession('in progress');
      } else if (mins === 0) {
        setTimeUntilSession('starting now!');
      } else if (mins < 60) {
        setTimeUntilSession(`in ${mins}m`);
      } else if (mins < 1440) {
        // Less than 24 hours
        const hours = Math.floor(mins / 60);
        const remainder = mins % 60;
        if (remainder === 0) {
          setTimeUntilSession(`in ${hours}h`);
        } else {
          setTimeUntilSession(`in ${hours}h ${remainder}m`);
        }
      } else {
        // 24 hours or more - show in days
        const days = Math.floor(mins / 1440);
        const remainingHours = Math.floor((mins % 1440) / 60);
        if (remainingHours === 0) {
          setTimeUntilSession(`in ${days}d`);
        } else {
          setTimeUntilSession(`in ${days}d ${remainingHours}h`);
        }
      }
    };

    // Initial update
    updateDisplay();

    // Decrement every minute
    const interval = setInterval(() => {
      setMinutesRemaining(prev => {
        const newValue = prev - 1;
        return newValue;
      });
    }, 60000);

    return () => clearInterval(interval);
  }, [session, minutesRemaining]);

  // Don't render if session has passed
  if (!session || timeUntilSession === null) {
    return null;
  }

  const coachName = session.coach?.displayName || session.coach?.display_name || 'Your Coach';
  const coachImage = session.coach?.profileImage || session.coach?.profile_image?.url;
  const sessionDuration = session.duration_minutes || 30;

  const formatSessionTime = () => {
    const start = new Date(session.scheduled_start);
    return start.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleJoinClick = () => {
    if (canJoin && onJoin) {
      onJoin(session);
    }
  };

  return (
    <div className={styles.banner}>
      <div className={styles.content}>
        <div className={styles.coachImage}>
          {coachImage ? (
            <img src={coachImage} alt={coachName} />
          ) : (
            <div className={styles.coachImageFallback}>
              <span>👤</span>
            </div>
          )}
        </div>

        <div className={styles.sessionInfo}>
          <div className={styles.coachName}>{coachName}</div>
          <div className={styles.sessionDetails}>
            <span className={styles.sessionTime}>{formatSessionTime()}</span>
            <span className={styles.sessionDuration}>• {sessionDuration} min</span>
          </div>
          <div className={styles.countdown}>{timeUntilSession}</div>
        </div>

        <div className={styles.actions}>
          <button
            className={canJoin ? styles.joinButtonActive : styles.joinButton}
            onClick={handleJoinClick}
            disabled={!canJoin}
          >
            JOIN SESSION
          </button>
        </div>
      </div>
    </div>
  );
}

export default SessionBanner;
